import React, { useRef, useEffect, useState } from 'react';

// Custom icons for text formatting
const BoldIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
  </svg>
);

const ItalicIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h8 M8 20h8 M15 4L9 20" />
  </svg>
);

const UnderlineIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 19h12M8 5v8a4 4 0 0 0 8 0V5" />
  </svg>
);

const LinkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 0 0-5.656 0l-4 4a4 4 0 1 0 5.656 5.656l1.102-1.101m-.758-4.899a4 4 0 0 0 5.656 0l4-4a4 4 0 0 0-5.656-5.656l-1.1 1.1" />
  </svg>
);

const BulletListIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
);

const NumberListIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h.01M3 8h.01M3 12h.01M8 4h13M8 8h13M8 12h13M3 16h.01M8 16h13M3 20h.01M8 20h13" />
  </svg>
);

const ParagraphBlock = ({ block, isActive, onChange, isRTL = false, language = 'en' }) => {
  const editorRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [savedSelection, setSavedSelection] = useState(null);
  
  // Support both old format (text + formatting) and new format (html)
  const { text = '', formatting = {}, html = '' } = block.content || {};
  const { color = '#374151' } = formatting; // Default to gray-700

  useEffect(() => {
    if (isActive && editorRef.current) {
      editorRef.current.focus();
      setShowToolbar(true);
    } else {
      setShowToolbar(false);
      setShowColorPicker(false);
    }
  }, [isActive]);

  useEffect(() => {
    // Initialize content if we have old format
    if (editorRef.current && text && !html) {
      const { bold = false, italic = false } = formatting;
      let formattedText = text;
      if (bold) formattedText = `<strong>${formattedText}</strong>`;
      if (italic) formattedText = `<em>${formattedText}</em>`;
      editorRef.current.innerHTML = formattedText;
    } else if (editorRef.current && html) {
      editorRef.current.innerHTML = html;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  };

  const restoreSelection = (range) => {
    if (range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      // Clean up the HTML and ensure external links open in new tab
      const cleanedHtml = processLinks(htmlContent);
      onChange({ 
        html: cleanedHtml,
        text: editorRef.current.textContent, // Keep text for backward compatibility
        formatting: { color }
      });
    }
  };

  const processLinks = (html) => {
    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Find all links and update their attributes
    const links = temp.querySelectorAll('a');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Check if link is external
        const isExternal = isExternalLink(href);
        if (isExternal) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        } else {
          link.removeAttribute('target');
          link.removeAttribute('rel');
        }
      }
    });
    
    return temp.innerHTML;
  };

  const isExternalLink = (url) => {
    try {
      // If URL doesn't have protocol, it's relative (internal)
      if (!url.match(/^https?:\/\//i)) {
        return false;
      }
      
      const linkHost = new URL(url).hostname;
      const currentHost = window.location.hostname;
      
      // Compare hosts (ignoring www prefix)
      const normalizedLinkHost = linkHost.replace(/^www\./, '');
      const normalizedCurrentHost = currentHost.replace(/^www\./, '');
      
      return normalizedLinkHost !== normalizedCurrentHost;
    } catch (e) {
      // If URL parsing fails, treat as internal
      return false;
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    handleInput();
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const toggleFormat = (format) => {
    execCommand(format);
  };

  const handleColorChange = (newColor) => {
    execCommand('foreColor', newColor);
    setShowColorPicker(false);
    
    // Update the color in formatting for the color picker display
    onChange({ 
      html: editorRef.current ? editorRef.current.innerHTML : html,
      text: editorRef.current ? editorRef.current.textContent : text,
      formatting: { color: newColor }
    });
  };

  const openLinkModal = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    // Check if we're clicking on an existing link
    let linkElement = null;
    if (selection.anchorNode) {
      linkElement = selection.anchorNode.parentElement;
      if (linkElement && linkElement.tagName !== 'A') {
        linkElement = linkElement.closest('a');
      }
    }
    
    if (linkElement && linkElement.tagName === 'A') {
      // Edit existing link
      setLinkUrl(linkElement.getAttribute('href') || '');
      setLinkText(linkElement.textContent || '');
    } else if (selectedText) {
      // Create new link from selection
      setLinkUrl('');
      setLinkText(selectedText);
    } else {
      // No selection, allow entering both text and URL
      setLinkUrl('');
      setLinkText('');
    }
    
    setSavedSelection(saveSelection());
    setShowLinkModal(true);
  };

  const insertLink = () => {
    if (!linkUrl) return;
    
    restoreSelection(savedSelection);
    
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    if (selectedText || linkText) {
      // Create link HTML
      const isExternal = isExternalLink(linkUrl);
      const targetAttr = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
      const linkHtml = `<a href="${linkUrl}"${targetAttr}>${linkText || selectedText}</a>`;
      
      // Insert the link
      if (selectedText) {
        execCommand('insertHTML', linkHtml);
      } else {
        // No selection, insert at cursor
        document.execCommand('insertHTML', false, linkHtml + '&nbsp;');
        handleInput();
      }
    }
    
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    setSavedSelection(null);
  };

  const removeLink = () => {
    execCommand('unlink');
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
  };

  const toggleList = (listType) => {
    // listType can be 'insertUnorderedList' or 'insertOrderedList'
    execCommand(listType);
  };

  const handleKeyDown = (e) => {
    // Handle Enter key - insert double br for paragraph spacing
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Insert double line break for better spacing
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      const br1 = document.createElement('br');
      const br2 = document.createElement('br');
      
      range.deleteContents();
      range.insertNode(br2);
      range.insertNode(br1);
      
      // Move cursor after the line breaks
      range.setStartAfter(br2);
      range.setEndAfter(br2);
      selection.removeAllRanges();
      selection.addRange(range);
      
      handleInput();
    }
  };

  // Predefined color palette
  const colorPalette = [
    { name: 'Gray', value: '#374151' },
    { name: 'Black', value: '#000000' },
    { name: 'Red', value: '#DC2626' },
    { name: 'Orange', value: '#EA580C' },
    { name: 'Yellow', value: '#CA8A04' },
    { name: 'Green', value: '#16A34A' },
    { name: 'Blue', value: '#2563EB' },
    { name: 'Indigo', value: '#4F46E5' },
    { name: 'Purple', value: '#9333EA' },
    { name: 'Pink', value: '#DB2777' },
  ];

  const textDirection = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'text-right' : 'text-left';

  const editorClassName = `w-full bg-transparent border-none outline-none text-base md:text-lg min-h-[1.5em] ${textAlign}`;

  return (
    <div className="paragraph-block relative">
      {/* Formatting Toolbar */}
      {showToolbar && (
        <div className={`flex items-center gap-1 mb-2 pb-2 border-b border-gray-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            onClick={() => toggleFormat('bold')}
            onMouseDown={(e) => e.preventDefault()}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
            title={isRTL ? 'מודגש' : 'Bold'}
          >
            <BoldIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => toggleFormat('italic')}
            onMouseDown={(e) => e.preventDefault()}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
            title={isRTL ? 'נטוי' : 'Italic'}
          >
            <ItalicIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => toggleFormat('underline')}
            onMouseDown={(e) => e.preventDefault()}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
            title={isRTL ? 'קו תחתון' : 'Underline'}
          >
            <UnderlineIcon className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          <button
            onClick={openLinkModal}
            onMouseDown={(e) => e.preventDefault()}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
            title={isRTL ? 'קישור' : 'Insert Link'}
          >
            <LinkIcon className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          <button
            onClick={() => toggleList('insertUnorderedList')}
            onMouseDown={(e) => e.preventDefault()}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
            title={isRTL ? 'רשימת תבליטים' : 'Bullet List'}
          >
            <BulletListIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => toggleList('insertOrderedList')}
            onMouseDown={(e) => e.preventDefault()}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
            title={isRTL ? 'רשימה ממוספרת' : 'Numbered List'}
          >
            <NumberListIcon className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1" />
          
          {/* Color Picker */}
          <div className="relative">
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              onMouseDown={(e) => e.preventDefault()}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors flex items-center gap-1"
              title={isRTL ? 'צבע טקסט' : 'Text Color'}
            >
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: color }}
              />
            </button>
            
            {showColorPicker && (
              <>
                {/* Backdrop to close on outside click */}
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setShowColorPicker(false)}
                />
                
                {/* Color Palette */}
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 grid grid-cols-5 gap-1">
                  {colorPalette.map((colorOption) => (
                    <button
                      key={colorOption.value}
                      onClick={() => handleColorChange(colorOption.value)}
                      className={`w-8 h-8 rounded border-2 transition-all hover:scale-110 ${
                        color === colorOption.value ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: colorOption.value }}
                      title={colorOption.name}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* ContentEditable Editor */}
      <div
        ref={editorRef}
        contentEditable={true}
        onInput={handleInput}
        onBlur={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={isRTL ? 'כתבו את הפסקה שלכם...' : 'Write your paragraph...'}
        dir={textDirection}
        className={editorClassName}
        style={{ 
          color: color,
          minHeight: '1.5em',
          lineHeight: '25px'
        }}
        suppressContentEditableWarning={true}
      />

      {/* Link Modal */}
      {showLinkModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
            onClick={() => setShowLinkModal(false)}
          >
            {/* Modal */}
            <div 
              className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">
                {isRTL ? 'הוספת קישור' : 'Insert Link'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isRTL ? 'טקסט' : 'Text'}
                  </label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    placeholder={isRTL ? 'טקסט הקישור' : 'Link text'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isRTL ? 'כתובת URL' : 'URL'}
                  </label>
                  <input
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="text-xs text-gray-500">
                  {isRTL 
                    ? 'קישורים חיצוניים יפתחו בכרטיסייה חדשה אוטומטית' 
                    : 'External links will automatically open in a new tab'}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={removeLink}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  {isRTL ? 'הסר קישור' : 'Remove Link'}
                </button>
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  {isRTL ? 'ביטול' : 'Cancel'}
                </button>
                <button
                  onClick={insertLink}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {isRTL ? 'הוסף' : 'Insert'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      <style dangerouslySetInnerHTML={{
        __html: `
          .paragraph-block [contenteditable][data-placeholder]:empty:before {
            content: attr(data-placeholder);
            color: #9CA3AF;
            pointer-events: none;
            position: absolute;
          }
          
          .paragraph-block [contenteditable] a {
            color: #2563EB;
            text-decoration: underline;
            cursor: pointer;
          }
          
          .paragraph-block [contenteditable] a:hover {
            color: #1D4ED8;
          }
          
          .paragraph-block [contenteditable] strong {
            font-weight: 700;
          }
          
          .paragraph-block [contenteditable] em {
            font-style: italic;
          }
          
          .paragraph-block [contenteditable] u {
            text-decoration: underline;
          }
          
          .paragraph-block [contenteditable] ul,
          .paragraph-block [contenteditable] ol {
            margin: 0.5em 0;
            padding-left: 2em;
            padding-right: 0;
          }
          
          .paragraph-block [contenteditable][dir="rtl"] ul,
          .paragraph-block [contenteditable][dir="rtl"] ol {
            padding-left: 0;
            padding-right: 2em;
          }
          
          .paragraph-block [contenteditable] li {
            margin: 0.25em 0;
            line-height: 1.6;
          }
          
          .paragraph-block [contenteditable] ul {
            list-style-type: disc;
          }
          
          .paragraph-block [contenteditable] ol {
            list-style-type: decimal;
          }
          
          .paragraph-block [contenteditable]:focus {
            outline: none;
          }
        `
      }} />
    </div>
  );
};

export default ParagraphBlock;
